import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import ConfirmationModal from '__components/Modals/ConfirmationModal';
import LegacyButton from '__components/Button';
import type { ChatEvent } from './chatEvents';
import { closeCopy } from './errorCopy';
import { selectCanReconnect, selectExplainedCloseReason } from './reducer';
import { MAX_TEXT_CHARS } from './protocol';
import type { usePiChat } from './usePiChat';
import { PiChatLog, type ToolItem } from './PiChatLog';
import type { ChatPhase } from './PiChatSkeleton';
import { PiChatComposer } from './PiChatComposer';
import {
  ButtonGroup,
  HeaderDivider,
  HeaderName,
  HeaderTitle,
  PanelHeader,
  PanelRoot,
  PanelSection,
  Row,
} from './styles';

export interface PiChatPanelProps<E extends ChatEvent> {
  chat: Pick<
    ReturnType<typeof usePiChat>,
    | 'connectionState'
    | 'closedReason'
    | 'closedParams'
    | 'items'
    | 'isStreaming'
    | 'returnedDrafts'
    | 'sendMessage'
    | 'resume'
    | 'addNotice'
    | 'abort'
  >;
  phase: ChatPhase;
  loadingRevealed: boolean;
  canSend: boolean;
  busy: boolean;
  onClose: () => void;
  onReconnect: () => void;
  title: string;
  label: string;
  placeholder: string;
  emptyPrompt: string;
  status: ReactNode;
  actions: ReactNode;
  aboveLog: ReactNode;
  events: E[];
  renderEvent: (event: E) => ReactNode;
  renderToolRow?: (item: ToolItem) => ReactNode;
  groupsTool?: (item: ToolItem) => boolean;
  className?: string;
}

export const PiChatPanel = <E extends ChatEvent>({
  chat,
  phase,
  loadingRevealed,
  canSend,
  busy,
  onClose,
  onReconnect,
  title,
  label,
  placeholder,
  emptyPrompt,
  status,
  actions,
  aboveLog,
  events,
  renderEvent,
  renderToolRow,
  groupsTool,
  className,
}: PiChatPanelProps<E>) => {
  const { t } = useTranslation();
  const { connectionState, closedReason, isStreaming, sendMessage, addNotice } =
    chat;
  const isLoading = phase === 'loading';
  const explainedCloseReason = selectExplainedCloseReason(chat);
  const closed = closeCopy(closedReason, chat.closedParams, t);

  const [confirmingClose, setConfirmingClose] = useState(false);
  const handleClose = () => {
    if (busy) {
      setConfirmingClose(true);
      return;
    }
    onClose();
  };

  const handleSendMessage = useCallback(
    async (text: string): Promise<boolean> => {
      const sent = await sendMessage(text);
      if (sent && text.trim().length > MAX_TEXT_CHARS) {
        addNotice(
          t('Message was shortened to {{max}} characters before sending.', {
            max: MAX_TEXT_CHARS,
          })
        );
      }
      return sent;
    },
    [addNotice, sendMessage, t]
  );

  return (
    <PanelRoot
      className={className}
      role="region"
      aria-label={label}
      data-qa-id="pi-chat"
    >
      <PanelHeader>
        <HeaderTitle>
          <Icon icon="text-ai" size="md" color="font-primary" />
          <HeaderName
            size="md"
            weight="semibold"
            color="font-primary"
            truncate
            qa={{ id: 'pi-chat-title' }}
          >
            {title}
          </HeaderName>
          {status}
        </HeaderTitle>
        <ButtonGroup>
          {actions}
          <HeaderDivider aria-hidden />
          <Button
            variant="text"
            color="tertiary"
            leftIcon="action-close"
            leftIconSettings={{
              size: 'sm',
              tooltipTitleOverride: '',
              tooltipDescriptionOverride: t('Close'),
              tooltipPosition: 'top',
            }}
            onClick={handleClose}
            qa={{ id: 'pi-chat-close' }}
          />
        </ButtonGroup>
      </PanelHeader>

      {connectionState === 'closed' ? (
        <PanelSection>
          <Row>
            <div className="flex-1 min-w-0">
              <Typography
                size="sm"
                color="font-primary"
                qa={{
                  id: `pi-chat-closed-${explainedCloseReason ?? 'other'}`,
                }}
              >
                {closed.text}
              </Typography>
              {closed.code ? (
                <Typography size="sm" color="font-placeholder" truncate>
                  {closed.code}
                </Typography>
              ) : null}
            </div>
            {selectCanReconnect(chat) ? (
              <Button
                size="small"
                onClick={onReconnect}
                qa={{ id: 'pi-chat-reconnect' }}
              >
                {t('Reconnect')}
              </Button>
            ) : null}
          </Row>
        </PanelSection>
      ) : null}

      {aboveLog}

      <PiChatLog
        phase={phase}
        loadingRevealed={loadingRevealed}
        items={chat.items}
        isStreaming={isStreaming}
        emptyPrompt={emptyPrompt}
        events={events}
        renderEvent={renderEvent}
        renderToolRow={renderToolRow}
        groupsTool={groupsTool}
      />

      <PiChatComposer
        placeholder={placeholder}
        disabledMessage={
          isLoading
            ? t('Connecting…')
            : connectionState === 'closed'
              ? t('Not connected')
              : undefined
        }
        canSend={canSend}
        returnedDrafts={chat.returnedDrafts}
        onSend={handleSendMessage}
        onFocus={chat.resume}
        isStreaming={isStreaming}
        onStop={chat.abort}
      />

      <ConfirmationModal
        show={confirmingClose}
        heading={t('Close AI Panel')}
        onHide={() => setConfirmingClose(false)}
        leftBtn={
          <LegacyButton
            variant="text"
            color="blue"
            data-qa-id="pi-chat-close-cancel"
          />
        }
        defaultLeftBtnText={t('Cancel')}
        actionButton={false}
        rightBtn={
          <LegacyButton color="red" data-qa-id="pi-chat-close-confirm">
            {t('Close')}
          </LegacyButton>
        }
        rightBtnClickHandler={() => {
          setConfirmingClose(false);
          onClose();
        }}
      >
        {t('The AI is still working. Closing now stops it.')}
      </ConfirmationModal>
    </PanelRoot>
  );
};
